import {CssBaseline} from '@mui/material';
import {ThemeProvider} from '@mui/material/styles';
import {ConfigProvider, theme as antdBaseTheme} from 'antd';
import SSOIframe from 'Api/SSOIframe';
import {AuthContextProvider} from 'Context/AuthContext';
import IdleContextProvider from 'Context/IdleTimerContext';
import {useThemeMode} from 'Context/ThemeModeContext';
import {store} from 'Redux/PersistStore';
import {agentInternalSSOLogin} from 'Redux/Slices/login';
import buildAntdTheme from 'Theme/buildAntdTheme';
import {LOGIN, SEARCH} from 'Utils/constants/Paths';
import {SSO_ERROR_TEXT} from 'Utils/constants/genericConstants';
import {startTokenRefreshTimer} from 'Utils/helpers/RefreshTokenScheduler';
import {createMemberObj, isIframeRequest, setStorageForIframe} from 'Utils/helpers/IFrameMethods';
import {setAccessToken, setServiceToken} from 'Utils/helpers/TokenStorage';
import React, {useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import Loading from './Components/Loading';
import {ContentStackContextProvider} from './Context/ContentStackContext';
import {DataContextProvider} from './Context/DataContext';
import NavigationRoutes from './Routes/Routes';
import {setGlobal} from './Utils/helpers/CSfetchGlobalData';

function App(props) {
  interface CustomizedState {
    from: {
      pathname: string;
    };
  }
  const [loading, setLoading] = useState(true);
  const [dynamicTheme, setDynamicTheme] = useState<any>(null);
  const [branding, setBranding] = useState<any>(null);
  const {mode} = useThemeMode();
  // Rebuilt locally from the already-fetched branding whenever the light/dark
  // toggle changes, so switching modes never re-hits the tenant theme JSON.
  // Before branding has loaded, fall back to just the mode's algorithm so the
  // boot-time loading screen still renders in the right light/dark tokens
  // instead of flashing antd's light defaults.
  const antdTheme = useMemo(
    () =>
      branding
        ? buildAntdTheme(branding, mode)
        : {
            algorithm:
              mode === 'dark' ? antdBaseTheme.darkAlgorithm : antdBaseTheme.defaultAlgorithm,
          },
    [branding, mode],
  );
  const [header, setHeader] = useState(null);
  const [artifacts, setartifacts] = useState(null);
  const [isIframeAccessible, setIsIframeAccessible] = useState(true);
  const [error, setError] = useState<boolean | string>();
  const location = useLocation();
  const state = location.state as CustomizedState;
  const from = state?.from?.pathname || LOGIN;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginInfo: any = useSelector((state: any) => state?.login?.loginInfo);

  // Keeps the Keycloak-issued access token alive for the whole session.
  // Covers every path that can populate Redux's loginInfo (internal SSO
  // below, SAML SSO in Pages/SSOLogin, and the legacy Pages/Login form) —
  // without this, the token silently expires and the next GraphQL call
  // (see errorLink in GQL/utils.ts) force-logs the agent out mid-session.
  useEffect(() => {
    if (loginInfo?.access_token) {
      startTokenRefreshTimer(loginInfo, store, navigate);
    }
  }, [loginInfo?.access_token]);

  useEffect(() => {
    window.addEventListener('mousedown', function () {
      document.body.classList.add('using-mouse');
    });

    window.addEventListener('keydown', function (event) {
      if (event.target instanceof HTMLInputElement) {
        if (event?.target?.tagName !== 'INPUT') {
          document.body.classList.remove('using-mouse');
        }
      }
    });
  }, []);

  useEffect(() => {
    if (props.token) {
      (async () => {
        let payload = {
          access_token: props.token,
          refresh_token: props.refreshToken,
        };
        let resp = await dispatch(agentInternalSSOLogin(payload)).unwrap();
        if (resp?.error || resp?.response?.data?.error) {
          navigate(from, {replace: true});
        } else {
          navigate(SEARCH, {replace: true});
        }
      })();
    }
  }, [props.token]);

  useLayoutEffect(() => {
    const CScall = async () => {
      const CSGlobalData: any = await setGlobal();
      if (CSGlobalData.error) {
        console.error(CSGlobalData.error);
        setError(true);
      }
      setartifacts(CSGlobalData.artifacts);
      setHeader(CSGlobalData.header);
      setDynamicTheme(CSGlobalData.finalTheme);
      setBranding(CSGlobalData.branding);
      setLoading(false);
    };
    CScall();
  }, []);

  /**
   * IFRAME setup part 1/2
   *
   * the request comes from iframe if 4 params are present: internaId, externalId, token, memberStatus and name.
   * Email is optional as there is a chance there is no email associated to the member,
   * in that case the gifting will not be possible.
   * If the tool is available for the Agent with specific role the useEffect saves the params in the session storage,
   * if not it shows a message to the agent.
   * next step is in dashboard file
   */
  const [searchParams] = useSearchParams();
  const {state: isIFrame, params, token, tenant} = isIframeRequest(searchParams);

  useEffect(() => {
    if (isIFrame) {
      (async () => {
        const data = await SSOIframe(tenant, token);
        if (data?.access_token) {
          const memberData = createMemberObj(params);
          setAccessToken(data?.access_token);
          setServiceToken(data?.access_token);
          setStorageForIframe(memberData);
        } else {
          setIsIframeAccessible(false);
        }
      })();
    }
  }, []);

  // ConfigProvider wraps every branch below (including the loading/error
  // states, via the mode-only fallback theme above) so nothing renders with
  // antd's light-mode defaults before the tenant branding finishes loading.
  return (
    <ConfigProvider theme={antdTheme}>
      {loading && <Loading isOpen={true} />}
      {!loading && error && <p style={{textAlign: 'center'}}>{SSO_ERROR_TEXT.default}</p>}
      {!loading && !error && !isIframeAccessible && (
        <p style={{textAlign: 'center'}}>Transactions Support not available.</p>
      )}
      {!loading && !error && isIframeAccessible && (
        <ContentStackContextProvider value={{header, artifacts}}>
          {/* antd components migrated off MUI read their theme from ConfigProvider above,
              mapped from the same tenant branding JSON as this MUI theme (see buildAntdTheme). */}
          <ThemeProvider theme={dynamicTheme}>
            <AuthContextProvider>
              <DataContextProvider>
                <IdleContextProvider>
                  <CssBaseline />
                  <NavigationRoutes />
                </IdleContextProvider>
              </DataContextProvider>
            </AuthContextProvider>
          </ThemeProvider>
        </ContentStackContextProvider>
      )}
    </ConfigProvider>
  );
}

export default App;
